import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = ({ onDemoRequest, onROICalculator }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [userCount, setUserCount] = useState(12847);

  const translations = {
    en: {
      headline: "Transform Your Aluminum Extrusion Business",
      subheadline: "Reduce Manual Work by 70% and Increase Profitability by 25% with India's Only ERP Built for Aluminum Manufacturers",
      primaryCTA: "Request Live Demo",
      secondaryCTA: "Calculate Your ROI",
      trustMetrics: {
        deployments: "500+ Successful Deployments",
        costReduction: "Average 23% Cost Reduction",
        activeUsers: "Active System Users"
      }
    },
    hi: {
      headline: "अपने एल्यूमीनियम एक्सट्रूज़न व्यवसाय को बदलें",
      subheadline: "भारत के एकमात्र एल्यूमीनियम निर्माताओं के लिए बने ERP के साथ मैन्युअल काम 70% कम करें और लाभप्रदता 25% बढ़ाएं",
      primaryCTA: "लाइव डेमो का अनुरोध करें",
      secondaryCTA: "अपना ROI गणना करें",
      trustMetrics: {
        deployments: "500+ सफल तैनाती",
        costReduction: "औसत 23% लागत में कमी",
        activeUsers: "सक्रिय सिस्टम उपयोगकर्ता"
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }

    // Simulate real-time user count updates
    const interval = setInterval(() => {
      setUserCount((prev) => prev + Math.floor(Math.random() * 3));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-primary-50 via-background to-surface-100 pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23000000%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%227%22 cy=%227%22 r=%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Column */}
          <div className="space-y-8">
            {/* Main Headline */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight">
                {t.headline}
              </h1>
              
              <p className="text-lg md:text-xl text-text-secondary leading-relaxed max-w-2xl">
                {t.subheadline}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                size="lg"
                onClick={onDemoRequest}
                className="bg-gradient-accent hover:bg-accent-600 shadow-cta hover-scale"
                iconName="Calendar"
                iconPosition="left"
                iconSize={20}>

                {t.primaryCTA}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={onROICalculator}
                className="border-primary text-primary hover:bg-primary-50"
                iconName="Calculator"
                iconPosition="left"
                iconSize={20}>

                {t.secondaryCTA}
              </Button>
            </div>

            {/* Trust Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  <span className="text-2xl font-bold text-primary">500+</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.deployments}</p>
              </div>
              
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="TrendingDown" size={20} color="var(--color-success)" />
                  <span className="text-2xl font-bold text-primary">23%</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.costReduction}</p>
              </div>
              
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="Users" size={20} color="var(--color-primary)" />
                  <span className="text-2xl font-bold text-primary">{userCount.toLocaleString()}</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.activeUsers}</p>
              </div>
            </div>
          </div>

          {/* Visual Column */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-lg p-8 border border-border">
              {/* Mock ERP Dashboard */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-primary">Production Dashboard</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-success rounded-full"></div>
                    <span className="text-sm text-text-secondary">Live</span>
                  </div>
                </div>
                
                {/* Mock Charts */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">Daily Output</span>
                      <Icon name="TrendingUp" size={16} color="var(--color-success)" />
                    </div>
                    <div className="text-2xl font-bold text-primary">2,847</div>
                    <div className="text-xs text-success">+12% from yesterday</div>
                  </div>
                  
                  <div className="bg-surface-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">Quality Score</span>
                      <Icon name="Award" size={16} color="var(--color-success)" />
                    </div>
                    <div className="text-2xl font-bold text-primary">98.5%</div>
                    <div className="text-xs text-success">+2.3% this month</div>
                  </div>
                </div>
                
                {/* Mock Progress Bars */}
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-text-secondary">Order Completion</span>
                      <span className="text-primary font-medium">87%</span>
                    </div>
                    <div className="w-full bg-surface-200 rounded-full h-2">
                      <div className="bg-gradient-accent h-2 rounded-full" style={{ width: '87%' }}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-text-secondary">Inventory Turnover</span>
                      <span className="text-primary font-medium">92%</span>
                    </div>
                    <div className="w-full bg-surface-200 rounded-full h-2">
                      <div className="bg-gradient-primary h-2 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 bg-success text-white rounded-full p-3 shadow-lg">
              <Icon name="CheckCircle" size={24} />
            </div>
            
            <div className="absolute -bottom-4 -left-4 bg-accent text-white rounded-full p-3 shadow-lg">
              <Icon name="TrendingUp" size={24} />
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;